import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Image,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ASSET_IDS,
  AssetConfig,
  AssetId,
  LAST_STAKE_ASSET_KEY,
  parseStakeMode,
} from "../../config/assets";
import { ThemeColor } from "../../theme/palette";
import {
  formatSecurityScore,
  SECURITY_SCORES,
  SecurityScore,
} from "./securityScores";

const BOX_BG = "#131B26";
const BOX_BG_HOVER = "#1a2433";
const TEXT_SECONDARY = "whiteAlpha.800";
const TEXT_LABEL = "whiteAlpha.700";

const animatedUnderlineLinkSx = {
  position: "relative",
  textDecoration: "none",
  display: "inline-block",
  pb: "4px",
  _after: {
    content: '""',
    display: "block",
    width: 0,
    height: "1px",
    bg: "#5FF4F4",
    transition: "width 0.3s ease",
    position: "absolute",
    left: 0,
    bottom: 0,
  },
  _hover: {
    textDecoration: "none",
    _after: { width: "100%" },
  },
};

const ASSET_ICONS: Record<AssetId, string> = {
  sol: "/solana.png",
  jto: "/jto.svg",
  kyros: "/kyros.svg",
};

const formatStakeTvl = (value: number): string => {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const formatApy = (apy: number | null, loading: boolean): string => {
  if (loading) return "—";
  if (apy === null || apy === 0) return "TBD";
  return `${(apy * 100).toFixed(1)}%`;
};

interface StakePageHeroProps {
  assetId: AssetId;
  config: AssetConfig;
  tvl: number;
  tvlLoading: boolean;
  apy: number | null;
  apyLoading: boolean;
  assetConfigs: Record<string, AssetConfig>;
}

const StatSpinner = () => (
  <Flex minH="36px" align="center">
    <Spinner size="md" color={ThemeColor.KAIROS} />
  </Flex>
);

const RiskRatingDisclaimer = ({ securityScore }: { securityScore: SecurityScore }) => (
  <VStack align="stretch" spacing={2}>
    <Text fontSize="sm" lineHeight="1.5" color="whiteAlpha.900">
      Risk ratings are provided by {securityScore.sourceName} and reflect assessed strategy and
      protocol risks. Ratings are indicative, may change over time, and are for informational
      purposes only. Not financial advice.
    </Text>
    <Link
      href={securityScore.sourceUrl}
      isExternal
      color={ThemeColor.KAIROS}
      fontSize="sm"
      fontWeight="semibold"
    >
      View full analysis on {securityScore.sourceName} →
    </Link>
  </VStack>
);

const RiskRatingInfoButton = ({ securityScore }: { securityScore: SecurityScore }) => (
  <Popover trigger="click" placement="top" closeOnBlur>
    <PopoverTrigger>
      <Box
        as="button"
        type="button"
        aria-label="Risk rating information"
        display="inline-flex"
        lineHeight={0}
        ml={0.5}
        bg="transparent"
        border="none"
        p={0}
        cursor="pointer"
        color={TEXT_LABEL}
        _hover={{ color: "white" }}
      >
        <InfoOutlineIcon boxSize="14px" />
      </Box>
    </PopoverTrigger>
    <PopoverContent
      bg={BOX_BG}
      border="1px solid"
      borderColor="whiteAlpha.200"
      borderRadius="lg"
      maxW="20rem"
      _focus={{ boxShadow: "none" }}
    >
      <PopoverBody p={4}>
        <RiskRatingDisclaimer securityScore={securityScore} />
      </PopoverBody>
    </PopoverContent>
  </Popover>
);

interface HeroStatCellProps {
  label: string;
  value: ReactNode;
  loading?: boolean;
  valueColor?: string;
  hasRightBorder?: boolean;
  labelExtra?: ReactNode;
}

const HeroStatCell = ({
  label,
  value,
  loading = false,
  valueColor = "white",
  hasRightBorder = false,
  labelExtra,
}: HeroStatCellProps) => (
  <GridItem
    display="flex"
    flexDirection="column"
    px={{ base: 4, sm: 6 }}
    py={5}
    borderRight={hasRightBorder ? "1px solid" : undefined}
    borderColor="whiteAlpha.100"
  >
    <HStack spacing={2} align="flex-start" minH="32px">
      <Text
        fontSize="xs"
        fontWeight="medium"
        color={TEXT_LABEL}
        letterSpacing="0.08em"
        lineHeight="1.3"
      >
        {label}
      </Text>
      {labelExtra}
    </HStack>
    <Flex flex={1} align="flex-end" minH="36px" mt={2}>
      {loading ? (
        <StatSpinner />
      ) : (
        <Text
          fontSize={{ base: "2xl", sm: "3xl" }}
          fontWeight="semibold"
          color={valueColor}
          lineHeight="1"
        >
          {value}
        </Text>
      )}
    </Flex>
  </GridItem>
);

export const StakePageHero = ({
  assetId,
  config,
  tvl,
  tvlLoading,
  apy,
  apyLoading,
  assetConfigs,
}: StakePageHeroProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentMode = parseStakeMode(searchParams.get("mode"));
  const securityScore = SECURITY_SCORES[assetId];
  const statColumns = securityScore ? "1fr 1fr 1fr" : "1fr 1fr";

  const selectAsset = (id: AssetId) => {
    localStorage.setItem(LAST_STAKE_ASSET_KEY, id);
    navigate(`/stake/${id}?mode=${currentMode}`);
  };

  return (
    <VStack spacing={6} align="stretch" width="100%">
      <Box>
        <HStack spacing={1} align="flex-start">
          <Text
            fontWeight="semibold"
            fontSize="5xl"
            color="white"
            lineHeight="1.1"
            letterSpacing="-0.02em"
          >
            Stake
          </Text>
        </HStack>
        <Text fontSize="md" color={TEXT_SECONDARY} mt={2}>
          MEV-boosted staking powered by Kyros.
        </Text>
      </Box>

      <HStack spacing={3} width="100%" align="stretch">
        {ASSET_IDS.map((id) => {
          const assetConfig = assetConfigs[id];
          const isActive = assetId === id;
          return (
            <Button
              key={id}
              flex={1}
              height="auto"
              py={3}
              px={3}
              onClick={() => selectAsset(id)}
              bg={BOX_BG}
              border="1px solid"
              borderColor={isActive ? ThemeColor.KAIROS : "whiteAlpha.200"}
              borderRadius="xl"
              _hover={{ bg: BOX_BG_HOVER }}
              _active={{ bg: BOX_BG }}
            >
              <HStack spacing={2} justify="center" width="100%">
                <Image
                  src={ASSET_ICONS[id]}
                  boxSize="22px"
                  borderRadius="full"
                  flexShrink={0}
                />
                <HStack spacing={1} fontSize="sm" whiteSpace="nowrap">
                  <Text color="white" fontWeight="medium">
                    {assetConfig.underlyingLabel}
                  </Text>
                  <Text color={TEXT_LABEL} fontSize="xs">
                    →
                  </Text>
                  <Text color={ThemeColor.KAIROS} fontWeight="medium">
                    {assetConfig.vrtName}
                  </Text>
                </HStack>
              </HStack>
            </Button>
          );
        })}
      </HStack>

      <Text
        fontSize="md"
        color={TEXT_SECONDARY}
        textAlign="center"
        lineHeight="1.6"
        px={2}
        w="100%"
      >
        {config.marketingDescription}
      </Text>

      <Box
        bg={BOX_BG}
        borderRadius="xl"
        border="1px solid"
        borderColor="whiteAlpha.200"
        overflow="hidden"
      >
        <Grid templateColumns={statColumns}>
          <HeroStatCell
            label="TOTAL VALUE LOCKED"
            value={formatStakeTvl(tvl)}
            loading={tvlLoading}
            hasRightBorder
          />
          <HeroStatCell
            label="APY"
            value={formatApy(apy, false)}
            loading={apyLoading}
            valueColor={ThemeColor.KAIROS}
            hasRightBorder={Boolean(securityScore)}
          />
          {securityScore ? (
            <HeroStatCell
              label="RISK RATING"
              value={
                <Link
                  href={securityScore.sourceUrl}
                  isExternal
                  sx={animatedUnderlineLinkSx}
                >
                  {formatSecurityScore(securityScore)}
                </Link>
              }
              valueColor="white"
              labelExtra={<RiskRatingInfoButton securityScore={securityScore} />}
            />
          ) : null}
        </Grid>
      </Box>
    </VStack>
  );
};
